import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Loader2 } from 'lucide-react';
import api from '@/lib/axios';
import { useSeo } from '../hooks/useSeo';

function Shell({ title, description, path, testId, children }) {
  useSeo({ title: `${title} – Marketplace TVDE`, description, path });
  return (
    <div className="min-h-screen bg-white text-slate-700" data-testid={testId}>
      <div className="mx-auto max-w-3xl px-4 py-12 md:px-6">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700">
          <ArrowLeft className="h-3 w-3" /> Voltar ao Marketplace
        </Link>
        <h1 className="mb-8 text-3xl font-extrabold text-slate-900 md:text-4xl">{title}</h1>
        <div className="space-y-5 text-sm leading-relaxed text-slate-600">{children}</div>
      </div>
    </div>
  );
}

export function LivreResolucaoPage() {
  const [f, setF] = useState({ nome: '', email: '', publicacao_id: '', pagamento_id: '', declaracao: '' });
  const [state, setState] = useState({ loading: false, msg: '', err: '' });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, msg: '', err: '' });
    try {
      const res = await api.post('/marketplace/livre-resolucao', f);
      setState({ loading: false, msg: res.data.message, err: '' });
      setF({ nome: '', email: '', publicacao_id: '', pagamento_id: '', declaracao: '' });
    } catch (err) {
      setState({ loading: false, msg: '', err: err.response?.data?.detail || 'Erro ao enviar pedido.' });
    }
  };

  return (
    <Shell
      title="Exercer direito de livre resolução"
      description="Formulário para exercer o direito de livre resolução (14 dias) de um anúncio pago no Marketplace TVDE."
      path="/livre-resolucao"
      testId="page-livre-resolucao"
    >
      <p>
        Enquanto consumidor, pode ter direito a resolver o contrato no prazo de 14 dias. Preencha o
        formulário abaixo. Analisaremos o pedido e, quando aplicável, procederemos ao reembolso através
        do meio de pagamento utilizado.
      </p>
      {state.msg ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" data-testid="lr-success">{state.msg}</div>
      ) : (
        <form onSubmit={submit} className="grid gap-3" data-testid="lr-form">
          <input className="rounded-lg border border-slate-300 px-3 py-2" placeholder="Nome" value={f.nome} onChange={(e) => set('nome', e.target.value)} data-testid="lr-nome" required />
          <input className="rounded-lg border border-slate-300 px-3 py-2" type="email" placeholder="Email usado no pagamento" value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="lr-email" required />
          <input className="rounded-lg border border-slate-300 px-3 py-2" placeholder="Identificador do anúncio (opcional)" value={f.publicacao_id} onChange={(e) => set('publicacao_id', e.target.value)} data-testid="lr-anuncio" />
          <input className="rounded-lg border border-slate-300 px-3 py-2" placeholder="Identificador do pagamento (opcional)" value={f.pagamento_id} onChange={(e) => set('pagamento_id', e.target.value)} data-testid="lr-pagamento" />
          <textarea className="rounded-lg border border-slate-300 px-3 py-2" rows={3} placeholder="Declaração de resolução" value={f.declaracao} onChange={(e) => set('declaracao', e.target.value)} data-testid="lr-declaracao" />
          {state.err && <p className="text-xs font-semibold text-red-600" data-testid="lr-error">{state.err}</p>}
          <button type="submit" disabled={state.loading} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-60" data-testid="lr-submit">
            {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enviar pedido
          </button>
        </form>
      )}
    </Shell>
  );
}

export function SegurancaPage() {
  const dicas = [
    'Não pague cauções antes de confirmar a identidade do anunciante.',
    'Confirme sempre DUA, seguro e inspeção da viatura.',
    'Veja a viatura presencialmente antes de qualquer pagamento.',
    'Celebre um contrato escrito entre as partes.',
    'Não envie documentos pessoais por canais suspeitos.',
    'Desconfie de preços muito abaixo do mercado.',
    'Nunca partilhe códigos de confirmação (email/SMS).',
    'Denuncie anúncios suspeitos através do botão "Denunciar anúncio".',
  ];
  return (
    <Shell
      title="Segurança e prevenção de burlas"
      description="Dicas de segurança e prevenção de fraude ao anunciar ou contactar no Marketplace TVDE."
      path="/seguranca"
      testId="page-seguranca"
    >
      <p className="inline-flex items-center gap-2 text-slate-700"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Siga estas recomendações para negociar em segurança:</p>
      <ul className="list-disc space-y-2 pl-5">
        {dicas.map((d) => <li key={d}>{d}</li>)}
      </ul>
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        O Marketplace é apenas intermediário e não é parte nos negócios. Em caso de suspeita de fraude,
        contacte-nos de imediato para suspensão do anúncio.
      </p>
    </Shell>
  );
}
